import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { uploadProfilePicture, updateMyProfile } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { User, Mail, Calendar, CreditCard, Target, Camera, Loader2, Edit, Check } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const fileRef = useRef(null);

  if (!user) {
    return null;
  }

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadProfilePicture(formData);
      toast.success('Profilbild aktualisiert!');
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) return;
    setSavingName(true);
    try {
      await updateMyProfile({ name: newName.trim() });
      toast.success('Name aktualisiert!');
      setEditingName(false);
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingName(false);
    }
  };

  const profilePicUrl = user.profile_picture
    ? (user.profile_picture.startsWith('http') ? user.profile_picture : `${API}/api/files/${user.profile_picture}`)
    : user.picture || null;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="profile-page">
      {/* Header */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            Mein <span className="text-primary">Profil</span>
          </h1>
        </div>
      </section>

      {/* Profile Content */}
      <section className="section-padding">
        <div className="container-max max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="card-surface">
                <CardContent className="p-6">
                  <h2 className="text-xl font-heading font-bold uppercase mb-6">Persönliche Daten</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-text-secondary">Name</p>
                        {editingName ? (
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              className="input-dark h-9"
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                              autoFocus
                              data-testid="edit-name-input"
                            />
                            <Button size="sm" className="btn-primary h-9 px-3" onClick={handleSaveName} disabled={savingName} data-testid="save-name-btn">
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-9 px-3" onClick={() => setEditingName(false)}>
                              Abbrechen
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{user.name || '-'}</p>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setNewName(user.name || ''); setEditingName(true); }} data-testid="edit-name-btn">
                              <Edit className="w-3.5 h-3.5 text-text-secondary" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center">
                        <Mail className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-text-secondary">E-Mail</p>
                        <p className="font-medium">{user.email}</p>
                      </div>
                    </div>

                    {user.phone && (
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-text-secondary">Telefon</p>
                          <p className="font-medium">{user.phone}</p>
                        </div>
                      </div>
                    )}

                    {user.address && (
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center">
                          <User className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <p className="text-sm text-text-secondary">Adresse</p>
                          <p className="font-medium">{user.address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Membership Info */}
              {user.member_number && (
                <Card className="card-surface">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-heading font-bold uppercase mb-6">Mitgliedschaft</h2>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center">
                          <Target className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <p className="text-sm text-text-secondary">Mitgliedsnummer</p>
                          <p className="font-heading font-bold text-lg text-accent">{user.member_number}</p>
                        </div>
                      </div>

                      {user.membership_type && (
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm text-text-secondary">Mitgliedschaftstyp</p>
                            <p className="font-medium capitalize">
                              {user.membership_type === 'monthly' ? 'Monatlich' : 'Jährlich'}
                            </p>
                          </div>
                        </div>
                      )}

                      {user.membership_start && (
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-accent" />
                          </div>
                          <div>
                            <p className="text-sm text-text-secondary">Mitglied seit</p>
                            <p className="font-medium">{formatDate(user.membership_start)}</p>
                          </div>
                        </div>
                      )}

                      {user.membership_end && (
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm text-text-secondary">Gültig bis</p>
                            <p className="font-medium">{formatDate(user.membership_end)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <div className="relative inline-block mb-4">
                    <Avatar className="w-24 h-24">
                      <AvatarImage src={profilePicUrl} alt={user.name} />
                      <AvatarFallback className="bg-primary/10 text-3xl font-heading font-bold text-primary">
                        {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadPhoto} />
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center hover:bg-primary-hover transition-colors"
                      data-testid="upload-profile-pic-btn"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                    </button>
                  </div>
                  <h3 className="font-heading font-bold uppercase">{user.name}</h3>
                  <div className="mt-2">
                    <Badge className={user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                      {user.is_active ? 'Aktiv' : 'Ausstehend'}
                    </Badge>
                  </div>
                  <p className="text-text-secondary text-sm mt-2 capitalize">{user.role}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;
